import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { ProjectType, PropertyType, ProjectStage, FieldDef } from 'app/core/models/account.types';

type RelatedToTypeValues = 'Property' | 'Projects';

@Component({
  selector: 'project-template-save',
  templateUrl: './project-template-save.component.html',
  styleUrls: ['./project-template-save.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectTemplateSaveComponent extends OnDestroyMixin implements OnInit {
  saveTemplateForm: UntypedFormGroup;
  addProjectByUrlForm: UntypedFormGroup;
  fieldDefs: FieldDef[] = [];
  project: Project;
  displayColumns: [];
  options: [];
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  projectTemplates: ProjectTemplate<any>[];
  isDealsDetailView: boolean = true;

  public uploadedFile: any;

  public projectTemplate: ProjectTemplate<any>;
  public projectId: number;
  public relatedToType: RelatedToTypeValues;

  /**
   * Constructor
   *
   * @param {MatDialogRef} matDialogRef
   * @param {FormBuilder} _formBuilder
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ProjectTemplateSaveComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _projectsService: ProjectsService,
    private _snackBar: MatSnackBar,
  ) {
    super();
  }

  ngOnInit(): void {
    this.saveTemplateForm = this._formBuilder.group({
      apply_layout: ['update'],
      name: [''],
    });

    this.projectTemplate = this.data.project_template;
    this.projectId = this.data.project_id;
    this.relatedToType = this.data.related_to_type;
  }

  /**
   * Discard the message
   */
  discard(state?): void {
    this.matDialogRef.close(state);
  }

  /**
   * Send the message
   */
  save(): void {
    this.saveTemplateForm.disable();
    if (this.saveTemplateForm.get('apply_layout').value == 'create') {
      const newProjectLayout = {
        name: this.saveTemplateForm.get('name').value,
        default: false,
        listed: true,
        definition: this.projectTemplate.definition,
        related_to_type: this.data.related_to_type,
      };

      const sanitizedTemplate = this.sanitizeTemplate(newProjectLayout);

      this._projectsService
        .createProjectTemplate(sanitizedTemplate)
        .pipe(untilComponentDestroyed(this))
        .subscribe((pt) => {
          this._projectsService
            .applyProjectTemplate(pt.id, [this.projectId])
            .pipe(untilComponentDestroyed(this))
            .subscribe((pt2) => {
              this._projectsService
                .getProjectTemplateById(pt.id, true)
                .pipe(untilComponentDestroyed(this))
                .subscribe((pt2) => {
                  this._snackBar.open('Template Created', '', {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  this.saveTemplateForm.enable();
                  this.discard('success');
                });
            });
        });
    } else if (this.saveTemplateForm.get('apply_layout').value == 'apply') {
      const newProjectLayout = {
        name: `${this.projectTemplate.name}`,
        default: false,
        listed: false,
        definition: this.projectTemplate.definition,
        related_to_type: this.data.related_to_type,
      };

      const sanitizedTemplate = this.sanitizeTemplate(newProjectLayout);

      this._projectsService
        .createProjectTemplate(sanitizedTemplate)
        .pipe(untilComponentDestroyed(this))
        .subscribe((pt) => {
          this._projectsService
            .applyProjectTemplate(pt.id, [this.projectId])
            .pipe(untilComponentDestroyed(this))
            .subscribe((pt2) => {
              this._projectsService
                .getProjectTemplateById(pt.id, true)
                .pipe(untilComponentDestroyed(this))
                .subscribe((pt2) => {
                  this._snackBar.open('Template Applied', '', {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                  this.saveTemplateForm.enable();
                  this.discard('success');
                });
            });
        });
    } else {
      const sanitizedTemplate = this.sanitizeTemplate(this.projectTemplate);

      this._projectsService
        .updateProjectTemplate(sanitizedTemplate)
        .pipe(untilComponentDestroyed(this))
        .subscribe((pt) => {
          this._snackBar.open('Template Saved', '', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
          this.saveTemplateForm.enable();
          this.discard('success');
        });
    }
  }

  private sanitizeTemplate(template: ProjectTemplate<any>): ProjectTemplate<any> {
    const templateStage = { ...template };

    templateStage?.definition?.dashboard?.tabs?.forEach((tab) => {
      const layoutPanelIds = tab.layout.map(({ id }) => id);
      tab.panels = (tab.panels || []).filter((panel) => layoutPanelIds.includes(panel.id));
    });

    if (templateStage?.definition?.dashboard?.panels?.length > 3) {
      const misplacedPanels = templateStage.definition.dashboard.panels
        .filter((_, i) => i > 2)
        .reduce((p, c) => [...p, Array.isArray(c) ? [...c] : [c]], [])
        .reduce((p, c) => [...p, ...c], []);
      const panels = (templateStage.definition.dashboard.panels as any[]).slice(0, 3);
      panels[panels.length - 1] = [...panels[panels.length - 1], ...misplacedPanels];
      templateStage.definition.dashboard.panels = [...panels];
    }

    return templateStage;
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }
}
